import type { SxProps, Theme } from "@mui/material/styles";

import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useState } from "react";
import { OAK_MONOSPACE_FONT_FAMILY } from "util/fontStacks";

import type { UndoFailureDisplay } from "./scoutDataChangeUndoFailure";

function StaffUndoFailureReason({ reason }: { reason: string }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "transparent",
        boxShadow: "none",
        "&::before": { display: "none" },
        mt: 0.5,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore fontSize="small" />}
        sx={{
          minHeight: 28,
          px: 0,
          "& .MuiAccordionSummary-content": { my: 0.25 },
        }}
      >
        <Typography variant="caption">Failure details (staff)</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Typography
          component="pre"
          variant="caption"
          sx={{
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: OAK_MONOSPACE_FONT_FAMILY,
          }}
        >
          {reason}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export function ScoutDataChangeUndoFailureAlert({
  display,
  onRetryStaleUndo,
  retryDisabled,
  onDismiss,
  sx,
}: {
  display: UndoFailureDisplay;
  onRetryStaleUndo?: () => Promise<void>;
  retryDisabled?: boolean;
  onDismiss: () => void;
  sx?: SxProps<Theme>;
}) {
  const { isStaff } = useCurrentUser();
  const [retrying, setRetrying] = useState(false);
  const [retryCompleted, setRetryCompleted] = useState(false);
  const showRetry = Boolean(display.isStale && onRetryStaleUndo && !retryCompleted);

  const handleRetry = async () => {
    if (!onRetryStaleUndo || retrying || retryDisabled) return;
    setRetrying(true);
    try {
      await onRetryStaleUndo();
      setRetryCompleted(true);
    } catch {
      // Keep Retry so they can send again.
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Alert
      severity="error"
      sx={sx}
      onClose={(event) => {
        event.stopPropagation();
        onDismiss();
      }}
    >
      <Stack spacing={1} alignItems="flex-start">
        <Typography variant="body2">{display.message}</Typography>
        {isStaff && display.staffReason ? (
          <StaffUndoFailureReason reason={display.staffReason} />
        ) : null}
        {showRetry && (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={retryDisabled || retrying}
            onClick={(event) => {
              event.stopPropagation();
              void handleRetry();
            }}
          >
            Retry
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

export function ShowUndoErrorButton({ onShow }: { onShow: () => void }) {
  return (
    <Button
      size="small"
      variant="text"
      onClick={(event) => {
        event.stopPropagation();
        onShow();
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      Show error
    </Button>
  );
}
